import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GatewayService } from '../gateway.service';
import { SocketService } from './socket.service';
import { LocalSaveService } from './local-save-service.service';

interface BadgeState {
    citasSinCoords: number;
}

@Injectable({ providedIn: 'root' })
export class BadgeCountService {
    private state$ = new BehaviorSubject<BadgeState>({ citasSinCoords: 0 });
    private initialized = false;

    public readonly citasSinCoords$: Observable<number>;

    constructor(
        private gateway: GatewayService,
        private socket: SocketService,
        private localSave: LocalSaveService
    ) {
        this.citasSinCoords$ = new Observable<number>(observer => {
            const sub = this.state$.subscribe(s => observer.next(s.citasSinCoords));
            return () => sub.unsubscribe();
        });
    }

    /** Llamar una sola vez tras login. Idempotente. */
    public init(): void {
        if (this.initialized) return;
        this.initialized = true;

        this.refreshCount();

        this.socket.onCitaSinCoordenadasNueva(() => this.bump(+1));
        this.socket.onCitaSinCoordenadasResuelta(() => this.bump(-1));

        // Polling de seguridad: cada 5 min refresca el count completo
        setInterval(() => this.refreshCount(), 5 * 60 * 1000);
    }

    public refreshCount(): void {
        // El badge debe reflejar exactamente lo que el usuario vera al entrar
        // al modulo: su contrata (cuando no es super admin) y el rango por
        // defecto de fechas (hoy -> manana).
        this.gateway.get('countCitasSinCoordenadas', this._buildFiltrosBadge()).then((res: any) => {
            const total = (res && typeof res.total === 'number') ? res.total : 0;
            this.state$.next({ citasSinCoords: total });
        }).catch(() => { /* swallow */ });
    }

    private _buildFiltrosBadge(): any {
        const user = this.localSave.get('usuarioPGV');
        const def = this._defaultRangoFechas();
        const body: any = {
            fechaDesde: def.desde,
            fechaHasta: def.hasta
        };
        // Scope por contrata para usuarios no-super-admin que sean distribuidor
        if (user && !user.esSuperAdmin && user.esDistribuidor && user.IDU) {
            body.id_contrata = [Number(user.IDU)];
        }
        return body;
    }

    private _defaultRangoFechas(): { desde: string, hasta: string } {
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        const fmt = (d: Date) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
        const hoy = new Date();
        const manana = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1);
        return { desde: fmt(hoy), hasta: fmt(manana) };
    }

    private bump(delta: number): void {
        const cur = this.state$.value.citasSinCoords;
        const next = Math.max(0, cur + delta);
        this.state$.next({ citasSinCoords: next });
    }
}
